<template>
    <Modal :show="show" :max-width="'7xl'" class="h-[90vh] overflow-hidden" @close="close">
        <div
            v-if="dropzoneHovered"
            class="absolute inset-0 z-50 flex items-center justify-center w-full h-full border-4 pointer-events-none bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200 border-neutral-100 dark:border-neutral-600"
        >
            <IllustratedMessage>
                <template #image>
                    <icon icon="hero/outline/search" class="w-12 h-12"></icon>
                </template>
                <template #title>{{ lang('media::media.gallery.drop') }} </template>
                {{ lang('media::media.gallery.drop_description') }}
            </IllustratedMessage>
        </div>
        <div ref="dropZoneRef" class="flex flex-col w-full h-full gap-px media-picker">
            <div
                class="relative flex items-center grow-0 gap-2 px-3 py-2 bg-gray-100 dark:bg-neutral-950 media-picker__bar"
                :class="[{ 'animate-pulse': loading.media }]"
            >
                <div class="flex-1 min-w-0">
                    <AdminText
                        v-model="search"
                        type="text"
                        :placeholder="lang('media::media.gallery.search_placeholder')"
                        @input="deferredSearch"
                    />
                </div>
                <div class="flex items-center flex-shrink-0 gap-1 text-sm">
                    <icon
                        v-if="loading.media || loading.info || loading.uploading"
                        icon="hero/outline/refresh"
                        class="w-4 h-4 mr-1 animate-spin"
                    ></icon>
                    <Button color="default" :disabled="!canPreviousPage" @click="previousPage">
                        <icon icon="hero/outline/chevron-left" class="size-4"></icon>
                    </Button>
                    <Button color="default" :disabled="!canNextPage" @click="nextPage">
                        <icon icon="hero/outline/chevron-right" class="size-4"></icon>
                    </Button>
                </div>
            </div>
            <main class="grid flex-auto grid-cols-12 gap-px overflow-hidden">
                <div v-if="showFolderSidebar" class="col-span-2 bg-gray-100 dark:bg-neutral-950 media-picker__sidebar">
                    <ul class="block">
                        <li>
                            <span
                                class="media-picker__folder"
                                :class="[
                                    !currentFolder
                                        ? 'bg-gray-200 text-black border-gray-200 dark:bg-neutral-800 dark:border-neutral-800 dark:text-neutral-50 media-picker__folder--active'
                                        : 'border-transparent text-gray-600 group-hover:text-black dark:text-neutral-400 dark:hover:text-white',
                                    'cursor-pointer flex space-x-3 items-center outline-hidden focus-visible:ring-1 ring-gray-300 focus-visible:z-10 group px-3 py-1 border group-hover:border-gray-300 select-none',
                                ]"
                                @click="selectFolder(null)"
                            >
                                <Icon
                                    icon="hero/outline/folder"
                                    class="w-3 h-3"
                                    :class="[`text-neutral-600 dark:text-neutral-400`]"
                                />
                                <span class="w-full text-sm text-current truncate transition">
                                    {{ lang('media::folders.all') }}
                                </span>
                            </span>
                        </li>
                        <li v-for="folder in folders" :key="folder.id">
                            <span
                                class="media-picker__folder"
                                :class="[
                                    currentFolder && folder.slug === currentFolder.slug
                                        ? 'bg-gray-200 text-black border-gray-200 dark:bg-neutral-800 dark:border-neutral-800 dark:text-neutral-50 media-picker__folder--active'
                                        : 'border-transparent text-gray-600 group-hover:text-black dark:text-neutral-400 dark:hover:text-white',
                                    'cursor-pointer flex space-x-3 items-center outline-hidden focus-visible:ring-1 ring-gray-300 focus-visible:z-10 group px-3 py-1 border group-hover:border-gray-300 select-none',
                                ]"
                                @click="selectFolder(folder)"
                            >
                                <Icon
                                    icon="hero/outline/folder"
                                    class="w-3 h-3"
                                    :class="[`text-${folder.color}-600`]"
                                />
                                <span class="w-full text-sm text-current truncate transition">
                                    {{ folder.name }}
                                </span>
                            </span>
                        </li>
                    </ul>
                </div>
                <div
                    class="h-full overflow-y-auto select-none"
                    :class="{
                        'col-span-12': !showSidebar && !showFolderSidebar,
                        'col-span-9': showSidebar && !showFolderSidebar,
                        'col-span-7': showSidebar && showFolderSidebar,
                        'col-span-10': !showSidebar && showFolderSidebar,
                    }"
                >
                    <ul
                        v-if="!loading.media && media && media.length > 0 && displayMode === 'grid'"
                        class="grid grid-cols-3 lg:grid-cols-6 media-picker__grid"
                        :class="[{ 'animate-pulse': loading.media || loading.uploading }]"
                    >
                        <li
                            v-for="(item, index) in media"
                            :key="item.id"
                            class="relative cursor-pointer aspect-square group media-picker__tile"
                            @click="selectMedia(item, index, $event)"
                        >
                            <div class="absolute inset-0 bg-gray-300 dark:bg-neutral-950 animate-pulse"></div>
                            <MediaPreview :media="item" class="bg-gray-300 dark:bg-neutral-950" />
                            <div
                                class="absolute inset-0 z-20 border-4 group-hover:border-blue-300 media-picker__ring"
                                :class="{
                                    'border-blue-600 is-selected': isSelected(item.id),
                                    'border-transparent': !isSelected(item.id),
                                }"
                            ></div>
                        </li>
                    </ul>
                    <ul
                        v-else-if="!loading.media && media && media.length > 0 && displayMode === 'list'"
                        class="grid grid-cols-1 gap-px pt-px"
                        :class="[{ 'animate-pulse': loading.media || loading.uploading }]"
                    >
                        <li
                            v-for="(item, index) in media"
                            :key="item.id"
                            class="relative cursor-pointer group media-picker__row"
                            @click="selectMedia(item, index, $event)"
                        >
                            <div
                                class="flex items-center justify-between p-2 border-2 border-transparent group-hover:border-blue-300"
                            >
                                <div class="flex items-center space-x-2 text-xs">
                                    <div class="size-12">
                                        <MediaPreview :media="item" class="bg-gray-300 dark:bg-neutral-950" />
                                    </div>
                                    <div>
                                        {{ item.name }}
                                    </div>
                                </div>
                            </div>
                            <div
                                class="absolute inset-0 z-20 border-2 group-hover:border-blue-300 media-picker__ring"
                                :class="{
                                    'border-blue-600 is-selected': isSelected(item.id),
                                    'border-transparent': !isSelected(item.id),
                                }"
                            ></div>
                        </li>
                    </ul>
                    <div
                        v-else-if="!loading.media && media && media.length === 0"
                        class="flex items-center justify-center h-full"
                    >
                        <IllustratedMessage>
                            <template #image>
                                <icon icon="hero/outline/search" class="w-12 h-12"></icon>
                            </template>
                            <template #title>{{ lang('media::media.gallery.no_results') }} </template>
                            {{ lang('media::media.gallery.no_results_description') }}
                        </IllustratedMessage>
                    </div>
                    <div v-else class="flex items-center justify-center h-full">
                        <IllustratedMessage>
                            <template #image>
                                <icon icon="hero/outline/refresh" class="w-12 h-12 animate-spin"></icon>
                            </template>
                            <template #title>{{ lang('media::media.gallery.loading') }} </template>
                            {{ lang('media::media.gallery.loading_description') }}
                        </IllustratedMessage>
                    </div>
                </div>
                <div v-if="showSidebar" class="flex flex-col h-full col-span-3">
                    <GalleryDetail :item="detail" @close="closeDetail" />
                </div>
            </main>
            <div class="grid items-center grow-0 grid-cols-12 px-2 py-2 bg-gray-100 dark:bg-neutral-950 media-picker__footer">
                <div class="col-span-2">
                    <AdminSwitchGroup v-model="displayMode" :options="displayModes" />
                </div>
                <div v-if="isFloatAdmin && selectedCount > 0" class="col-span-2 media-picker__count">
                    {{ lang('media::media.gallery.selected_count', { count: selectedCount }) }}
                </div>
                <div class="flex justify-end col-span-2 col-start-11 space-x-1">
                    <Button color="default" @click="close">{{ lang('general.close') }}</Button>
                    <Button color="primary" :disabled="!hasSelection" @click="submit">{{
                        lang('media::media.actions.select')
                    }}</Button>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick, onBeforeUnmount } from 'vue';
import { lang } from '@/lang/composable';
import type { SavedFile } from '@src/Common/Components/Fields/types';
import GalleryDetail from './GalleryDetail.vue';
import MediaPreview from './MediaPreview.vue';
import { useDropZone } from '@vueuse/core';
import { router, usePage } from '@inertiajs/vue3';

type FileValue = File | FileList | SavedFile | (File | SavedFile)[] | null;

const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'select', item: any): void;
    (event: 'newSelected', item: any): void;
    (event: 'deselected', item: any): void;
    (event: 'detail', item: any): void;
}>();

interface Props {
    show?: boolean;
    selected?: FileValue;
    multiple?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    show: true,
    selected: null,
    multiple: false,
});

const show = ref(props.show);
const search = ref('');
const pagination = ref({
    total: 0,
    page: 1,
    take: 60,
    last_page: 1,
});

const selected = ref(props.selected);
const newSelected = ref([]);
const deselected = ref([]);

const loading = ref({
    media: false,
    info: false,
    uploading: false,
});

const dropZoneRef = ref<HTMLDivElement>();

const ongoingUpload = ref(false);

const showFolderSidebar = ref(true);

const close = () => {
    show.value = false;

    emit('close');
};

const submit = () => {
    if (!hasSelection.value) {
        return;
    }

    emit('select', selected.value);
    emit('newSelected', newSelected.value);
    emit('deselected', deselected.value);

    close();
};

const media = ref([]);
const folders = ref([]);

const hasSelection = computed(() => {
    if (props.multiple) {
        return Array.isArray(selected.value) && selected.value.length > 0;
    }

    return selected.value != null;
});

const isFloatAdmin = computed(() => (usePage().props.theme as { admin?: string } | undefined)?.admin === 'float');

const selectedCount = computed(() => {
    if (props.multiple) {
        return Array.isArray(selected.value) ? selected.value.length : 0;
    }

    return selected.value != null ? 1 : 0;
});

const include = computed(() => {
    if (props.multiple) {
        return selected.value?.map((item) => (item ? item.id : null)).join(',');
    }

    return selected.value?.id;
});

/**
 * Folder select
 */
const currentFolder = ref(null);

const selectFolder = (folder) => {
    currentFolder.value = folder;
    loadMedia();
};


const loadMedia = () => {
    loading.value.media = true;
    axios
        .get(route('media.admin.api.gallery'), {
            params: {
                search: search.value,
                page: pagination.value.page,
                include: include.value,
                folder: currentFolder.value?.id ?? null,
            },
        })
        .then(({ data }) => {
            media.value = data.results;
            pagination.value = {
                total: data.pagination.total,
                page: data.pagination.page,
                take: data.pagination.take,
                last_page: data.pagination.last_page,
            };
            folders.value = data.folders;
            loading.value.media = false;
        });
};

const canPreviousPage = computed(() => pagination.value.page > 1);
const canNextPage = computed(() => pagination.value.page < pagination.value.last_page);

const previousPage = () => {
    if (canPreviousPage.value) {
        pagination.value.page -= 1;
        loadMedia();
    }
};

const nextPage = () => {
    if (canNextPage.value) {
        pagination.value.page += 1;
        loadMedia();
    }
};

let searchTimeout = null;

const deferredSearch = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
        pagination.value.page = 1;
        loadMedia();
    }, 300);
};

const lastSelectedIndex = ref<number | null>(null);

/**
 * @param {object} item - media item
 * @param {number} index - index of media item
 * @param {MouseEvent} event - click event to figure out (metaKey, ctrlKey, shiftKey)
 */
const selectMedia = (item, index, event) => {
    loading.value.info = true;

    axios.get(route('media.admin.api.media.info', { id: item.id })).then(({ data }) => {
        emit('detail', data);

        // Single select
        if (!props.multiple) {
            selected.value = data;
            newSelected.value = [data];
            detail.value = data;
            openDetail();
            loading.value.info = false;
            return;
        }

        // Multiple select
        const isShift = event.shiftKey;
        const isCmd = event.metaKey || event.ctrlKey;

        if (isShift && lastSelectedIndex.value !== null) {
            const start = Math.min(lastSelectedIndex.value, index);
            const end = Math.max(lastSelectedIndex.value, index);

            const rangeItems = media.value.slice(start, end + 1);

            const alreadySelected = Array.isArray(selected.value) ? selected.value : [];

            const newSelection = [...alreadySelected];
            rangeItems.forEach((it) => {
                if (!newSelection.some((sel) => sel.id === it.id)) {
                    newSelection.push(it);
                }
            });

            selected.value = newSelection;

            const newlyAdded = rangeItems.filter((r) => !alreadySelected.some((sel) => sel.id === r.id));
            newSelected.value = [...newSelected.value, ...newlyAdded];

            lastSelectedIndex.value = index;
        } else if (isCmd) {
            if (isSelected(item.id)) {
                selected.value = selected.value.filter(
                    (selectedItem) => parseInt(selectedItem.id) !== parseInt(item.id),
                );
                deselected.value = [...deselected.value, data];
            } else {
                selected.value = [...selected.value, data];
                newSelected.value = [...newSelected.value, data];

                lastSelectedIndex.value = index;
            }
        } else {
            if (isSelected(item.id)) {
                selected.value = selected.value.filter(
                    (selectedItem) => parseInt(selectedItem.id) !== parseInt(item.id),
                );
                deselected.value = [...deselected.value, data];
            } else {
                selected.value = [...selected.value, data];
                newSelected.value = [...newSelected.value, data];

                lastSelectedIndex.value = index;
            }
        }

        detail.value = data;
        openDetail();

        loading.value.info = false;
    });
};

const isSelected = (id) => {
    if (props.multiple) {
        return selected.value?.some((item) => (item ? parseInt(item.id) === id : false));
    }

    return parseInt(selected.value?.id) === id;
};

/**
 * Upload image to server
 */
const uploadFile = async (file) => {
    const data = new FormData();
    data.append('file', file);
    return axios.post(route('media.admin.api.create'), data).then(({ data }) => data.object);
};

const accept = ref('image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv');

async function onFileDrop(files: File[] | null) {
    if (!files) return;

    const acceptedFiles = files.filter((file) => {
        const acceptList = accept.value.split(',').map((item) => item.trim());

        // If acceptList is empty or contains 1 empty '' item, accept all files
        if (acceptList.length === 0 || (acceptList.length === 1 && acceptList[0] === '')) {
            return true;
        }

        return acceptList.some((type) => {
            if (type === 'image/*') {
                return file.type.startsWith('image/');
            } else if (type.startsWith('.')) {
                return file.name.endsWith(type);
            } else {
                return file.type === type;
            }
        });
    });

    if (acceptedFiles.length === 0) return;

    loading.value.uploading = true;
    ongoingUpload.value = true;

    try {
        for (const file of acceptedFiles) {
            const uploadedMedia = await uploadFile(file);
            media.value.unshift(uploadedMedia);
        }
    } finally {
        loading.value.uploading = false;
        ongoingUpload.value = false;
    }
}

const dropzoneHovered = ref(false);

const initDropzone = () => {
    const { isOverDropZone } = useDropZone(dropZoneRef.value, {
        onDrop: onFileDrop,
    });

    watch(
        isOverDropZone,
        (val) => {
            dropzoneHovered.value = val;
        },
        { immediate: true },
    );
};

/**
 * Sidebar
 */
const detail = ref(null);
const showSidebar = ref(false);

const openDetail = () => {
    showSidebar.value = true;
};

const closeDetail = () => {
    showSidebar.value = false;
};

watch(
    () => show.value,
    async (value) => {
        if (value) {
            loadMedia();

            await nextTick();
            initDropzone();
        }
    },
    { immediate: true },
);

/**
 * Prevent navigation if changes are not saved
 */
const navigationEventListener = ref(null);

const navigateAwayCheck = (event) => {
    // If changes are saved, let the user navigate
    if (!ongoingUpload.value) {
        if (navigationEventListener.value) {
            navigationEventListener.value();
        }

        return;
    }

    // If changes are not saved, ask the user if they want to leave
    if (ongoingUpload.value && !confirm(lang('media::media.messages.ongoing_upload'))) {
        event.preventDefault();
    } else {
        if (navigationEventListener.value) {
            navigationEventListener.value();
        }
    }

    return;
};

navigationEventListener.value = router.on('before', navigateAwayCheck);

/**
 * Display mode
 */
const displayModes = [
    { id: 'grid', name: lang('media::media.gallery.grid'), icon: 'hero/outline/squares-2x2' },
    { id: 'list', name: lang('media::media.gallery.list'), icon: 'hero/outline/table-cells' },
];

const displayMode = ref(displayModes[0].id);

onBeforeUnmount(() => {
    if (navigationEventListener.value) {
        navigationEventListener.value();
    }
});
</script>
